import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

// ICONS
import {
    faChevronDown,
    faChevronUp,
    faFilter,
    faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// TRANSLATIONS
import { useTranslation } from 'react-i18next';

// HELPERS
import { parse, stringify } from 'qs';
import {
    createNewEntityFromChildren,
    recursiveMap,
} from '../../Helpers/recursions';

// COMPONENTS
import Button from './Buttons/Button';

// STYLE
import style from '../../../scss/components/search.module.scss';

const Search = ({ entity, onChange, children, ...props }) => {
    const { t } = useTranslation();

    // IMMUTABLE STATE
    const [fields] = useState(createNewEntityFromChildren(children));

    // MUTABLE STATE
    const parsed = parse(window.location.search.replace('?', ''));
    const cleaned = Object.fromEntries(
        Object.entries(parsed).filter(
            ([, value]) => value !== '' && value != null,
        ),
    );

    const [params, setParams] = useState({
        ...fields,
        ...entity,
        ...cleaned,
    });

    // CALLBACKS
    const update = (change) => {
        const { nativeEvent } = change;
        setParams((params) => ({
            ...params,
            ...(nativeEvent != null ? nativeEvent : change),
        }));
    };

    // SEARCH
    const [activeSearch, setSearch] = useState(false);
    const [height, setHeight] = useState({ height: 0, overflow: 'hidden' });

    const toggleAccordion = () => setSearch((activeSearch) => !activeSearch);

    useEffect(() => {
        setHeight(
            activeSearch
                ? { height: 0, overflow: 'hidden' }
                : { height: '100%', overflow: 'visible' },
        );
    }, [activeSearch]);

    useEffect(() => {
        const query = stringify(params, {
            arrayFormat: 'brackets',
            encodeValuesOnly: true,
            skipNulls: true,
            filter: (prefix, value) => {
                if ((value && value.id) || null) {
                    return value.id;
                }

                if (typeof value === 'boolean' && value) {
                    return 1;
                }

                if (Array.isArray(value)) {
                    const normalized = value
                        .map((item) =>
                            item && typeof item === 'object' && 'id' in item
                                ? item.id
                                : item,
                        )
                        .filter((item) => item !== '' && item != null);

                    return normalized.length ? normalized : undefined;
                }

                if (!value) {
                    return undefined;
                }

                return value;
            },
        });

        window.history.replaceState(
            { at: Date.now() },
            '',
            query
                ? `${window.location.pathname}?${query}`
                : window.location.pathname,
        );
    }, [params]);

    const normalizeParamsForApi = (input) =>
        Object.fromEntries(
            Object.entries(input)
                .map(([key, value]) => {
                    if (value == null || value === '') return [key, undefined];

                    if (Array.isArray(value)) {
                        const normalized = value
                            .map((item) =>
                                item && typeof item === 'object' && 'id' in item
                                    ? item.id
                                    : item,
                            )
                            .filter((item) => item != null && item !== '');

                        return [
                            key,
                            normalized.length ? normalized : undefined,
                        ];
                    }

                    if (value && typeof value === 'object') {
                        return [key, 'id' in value ? value.id : undefined];
                    }

                    return [key, value];
                })
                .filter(([, value]) => value !== undefined),
        );

    useEffect(() => {
        onChange(normalizeParamsForApi(params));
    }, [params]);

    return (
        <div className={style.searchWrapper}>
            <div className={style.searchHeader}>
                <div className={style.searchTitle}>
                    <FontAwesomeIcon icon={faFilter} />
                    <h2>{t('application.filter')}</h2>
                </div>
                <Button
                    icon={activeSearch ? faChevronDown : faChevronUp}
                    name="search-open"
                    click={toggleAccordion}
                    unlabeled
                />
            </div>

            <form className={style.searchForm} style={height}>
                {recursiveMap(children, (child) =>
                    React.cloneElement(child, {
                        ...props,
                        errors: {},
                        entity: params,
                        onChange: update,
                    }),
                )}

                <div className={style.searchResetWrapper}>
                    <Button
                        className={style.searchReset}
                        name="reset"
                        icon={faTrash}
                        click={() =>
                            setParams((prev) => {
                                const nextParams = { ...fields };

                                if (!fields.type && prev.type) {
                                    nextParams.type = prev.type;
                                }

                                return nextParams;
                            })
                        }
                        unlabeled
                    />
                </div>
            </form>
        </div>
    );
};

export default Search;

Search.propTypes = {
    /**
     * Type of entity
     */
    entity: PropTypes.object.isRequired,
    /**
     * Type of onChange
     */
    onChange: PropTypes.func.isRequired,
    /**
     * Type of children
     */
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]).isRequired,
};
